<template>
  <div class="body flex flex-col gap-y-4">
    <p class="title3-bold">Vehicle details</p>

    <WInput
      v-model="contactDetails.vehicle.licensePlate"
      autofocus
      type="text"
      name="licensePlate"
      label="Registration number"
      :error="licensePlateError"
      @update:model-value="validateLicensePlate"
    />

    <p class="title3-bold mt-4">Third party driver details</p>

    <WInput
      v-model="contactDetails.firstName"
      type="text"
      name="firstName"
      autocomplete="given-name"
      label="First name"
      @update:model-value="capitaliseName"
    />

    <WInput
      v-model="contactDetails.lastName"
      type="text"
      name="lastName"
      autocomplete="family-name"
      label="Last name"
      @update:model-value="capitaliseName"
    />

    <WInput
      v-model="contactDetails.email"
      type="email"
      inputmode="email"
      name="email"
      autocmplete="email"
      label="Email"
      :error="emailError"
      @update:model-value="validateEmail"
    />

    <PhoneInput
      :phone="contactDetails.phone.formatted"
      label="Phone"
      placeholder=""
      @phone-changed="phoneChange"
      @error="setError"
    />

    <p class="title3-bold mt-4">Address details</p>

    <Address @place-changed="locationChange" />

    <p class="title3-bold mt-4">Insurance details</p>

    <WInput v-model="contactDetails.insurance.provider" type="text" name="insurance" label="Insurance Provider" />

    <WInput v-model="contactDetails.insurance.policy" type="text" name="policy" label="Policy Number" />

    <DatePicker
      v-model="contactDetails.insurance.validFrom"
      label="Valid from"
      @update:model-value="validateDate"
      @error="setError"
    />
    <DatePicker
      v-model="contactDetails.insurance.validTo"
      label="Valid to"
      :max="dayjs().add(5, 'years').format('YYYY-MM-DD')"
      :error="dateError"
      @update:model-value="validateDate"
      @error="setError"
    />
  </div>
</template>

<script setup lang="ts">
  import { watchEffect } from 'vue';
  import { FlattenObjectKeys, ThirdPartyDriver } from 'src/types/overlays';
  import { useOverlays } from 'src/composables/overlays';
  import dayjs from 'dayjs';
  import Address from '../Address.vue';
  import DatePicker from '../DatePicker.vue';
  import PhoneInput from '../PhoneInput.vue';
  import WInput from '../Input.vue';
  import { checkDeeplyNestedObjectProperties } from 'src/utils/helpers';

  const props = defineProps<{
    driver: ThirdPartyDriver;
    required?: FlattenObjectKeys<ThirdPartyDriver> | FlattenObjectKeys<ThirdPartyDriver>[];
  }>();

  const emit = defineEmits<{
    (e: 'error', error: boolean): void;
  }>();

  const {
    contactDetails,
    licensePlateError,
    emailError,
    dateError,
    generalError,
    validateLicensePlate,
    validateEmail,
    validateDate,
    capitaliseName,
    locationChange,
    phoneChange,
    setError,
  } = useOverlays(props.driver);

  watchEffect(() => {
    if (!!licensePlateError.value || !!emailError.value || !!dateError.value || generalError.value) {
      emit('error', true);
      return;
    }
    if (!checkDeeplyNestedObjectProperties(contactDetails.value as ThirdPartyDriver, props.required)) {
      emit('error', true);
      return;
    }
    emit('error', false);
  });
</script>

<style scoped></style>
